// Pure Entscheidungslogik für den Touch-Selektionsmodus im Kalender-Grid
// (Option B, Vorbild Google Calendar mobil): Tap selektiert einen Block,
// erst der selektierte Block ist per Direkt-Drag verschieb-/resizebar.
// Alles hier ist bewusst DOM-frei und wird von hour-grid.tsx verdrahtet —
// Desktop-Pfade (Maus/Pen) laufen an diesen Funktionen komplett vorbei.

/** Erlaubter Finger-Jitter (px), bevor ein Tap zum Scroll/Drag wird. */
export const TOUCH_MOVE_SLOP = 10

/** Long-Press-Schwelle (ms) — armiert Selektion + Drag in einem. */
export const LONG_PRESS_MS = 400

/** Hit-Zonen-Höhe (px) der großen Resize-Griffe am selektierten Block.
 *  Die Griffe sitzen mittig auf der Blockkante (je zur Hälfte außerhalb). */
export const TOUCH_RESIZE_HIT = 32

/** Wie weit die Griff-Hit-Zone über die Blockkante hinausragt (px). */
export const TOUCH_RESIZE_OVERHANG = TOUCH_RESIZE_HIT / 2

/** Bewegung innerhalb des Slops? (Betragsweise, beide Achsen.) */
export function isTapMovement(dx: number, dy: number, slop = TOUCH_MOVE_SLOP): boolean {
  return Math.abs(dx) <= slop && Math.abs(dy) <= slop
}

/**
 * Klassifiziert ein beendetes Pointer-Paar (down→up) auf einem Block:
 * 'tap' = Selektion setzen/wechseln. Nicht-Touch-Pointer liefern immer
 * 'none' — der Desktop-Pfad (Direkt-Drag) bleibt davon unberührt.
 */
export function classifyBlockTouchEnd(opts: {
  pointerType: string
  dx: number
  dy: number
  longPressFired: boolean
}): 'tap' | 'none' {
  if (opts.pointerType !== 'touch') return 'none'
  if (opts.longPressFired) return 'none'
  if (!isTapMovement(opts.dx, opts.dy)) return 'none'
  return 'tap'
}

/**
 * Selektions-Übergang: Tap ins Leere (tappedId=null) deselektiert, Tap auf
 * einen Block selektiert ihn (Wechsel eingeschlossen, idempotent bei
 * erneutem Tap auf denselben Block). Der aktuelle Zustand steht bewusst in
 * der Signatur — die Übergangsmatrix ist damit vollständig testbar.
 */
export function nextSelectedId(_current: string | null, tappedId: string | null): string | null {
  if (tappedId === null) return null
  return tappedId
}

/** Guard-Fenster (ms) nach einem SELEKTIERENDEN Tap: der Browser synthetisiert
 *  nach pointerup einen click an denselben Koordinaten — der träfe die soeben
 *  eingeblendete Action-Bar (Teilen/Löschen!). Innerhalb des Fensters werden
 *  Bar-Clicks geschluckt; ein bewusster zweiter Tap liegt immer darüber. */
export const SELECTION_CLICK_GUARD_MS = 350

/** Ist dieser Click der Geist des selektierenden Taps? (Nur ein Tap, der die
 *  Selektion NEU setzt, stempelt selectedAt — Taps auf einen bereits
 *  selektierten Block bedienen die Bar sofort.) */
export function isSelectionGhostClick(
  selectedAt: number | null,
  now: number,
  guard = SELECTION_CLICK_GUARD_MS,
): boolean {
  if (selectedAt === null) return false
  return now >= selectedAt && now - selectedAt < guard
}

/** Default-Dauer (min) eines Long-Press-Create ohne Aufzieh-Drag —
 *  identisch zum Desktop-Klick auf freie Spaltenfläche (Start + 1h). */
export const LONG_PRESS_CREATE_DEFAULT_MINS = 60

/**
 * Long-Press-Create: Hat der Finger nach dem Feuern weit genug gezogen, um
 * die Dauer aufzuspannen? Schwelle ist EIN Snap-Schritt in Pixeln (15min bei
 * aktueller Stundenhöhe) — darunter gilt die Geste als stationär und die
 * Endzeit fällt auf den Default (Start + 1h) zurück. Betragsweise: nach oben
 * ziehen (negatives Delta) zählt genauso wie nach unten.
 */
export function isCreateDragSizing(dragPx: number, hourHeight: number, snapMinutes = 15): boolean {
  return Math.abs(dragPx) >= (snapMinutes / 60) * hourHeight
}

/**
 * Zeitspanne des Long-Press-Create in Minuten seit 00:00. Beide Eingaben
 * sind bereits gesnappt (yToMinutes). Aufgezogen (`sized`): min/max von
 * Anker und Fingerposition — nach oben ziehen lässt den Start wandern,
 * das Ende bleibt am Anker; Mindestdauer ein Snap-Schritt (nie ein
 * 0-Minuten-Eintrag). Stationär: Anker + Default-Dauer, exakt wie der
 * Desktop-Klick. Überlauf über Mitternacht bleibt bewusst unclamped —
 * makeDateTime rollt in den Folgetag (Desktop-Parität).
 */
export function longPressCreateSpan(opts: {
  anchorMins: number
  currentMins: number
  sized: boolean
  snapMinutes?: number
  defaultMins?: number
}): { startMins: number; endMins: number } {
  const snap = opts.snapMinutes ?? 15
  const fallback = opts.defaultMins ?? LONG_PRESS_CREATE_DEFAULT_MINS
  if (!opts.sized) {
    return { startMins: opts.anchorMins, endMins: opts.anchorMins + fallback }
  }
  const startMins = Math.min(opts.anchorMins, opts.currentMins)
  let endMins = Math.max(opts.anchorMins, opts.currentMins)
  if (endMins - startMins < snap) endMins = startMins + snap
  return { startMins, endMins }
}

export type ResizeZone = 'start' | 'end' | 'move'

/**
 * Hit-Test am SELEKTIERTEN Block: entscheidet aus der Pointer-Position
 * relativ zur Block-Oberkante (offsetY, darf negativ sein — die Griffe
 * ragen TOUCH_RESIZE_OVERHANG px über die Kanten hinaus), ob ein
 * pointerdown Start-Resize, End-Resize oder Move bedeutet.
 * Bei sehr kurzen Blöcken clampen die Zonen an der Blockmitte
 * (obere Hälfte = start, untere = end; Move dann nur via Long-Press).
 */
export function resizeZoneAt(
  offsetY: number,
  blockHeight: number,
  hit = TOUCH_RESIZE_HIT,
): ResizeZone {
  const inside = hit - TOUCH_RESIZE_OVERHANG
  const startMax = Math.min(inside, blockHeight / 2)
  const endMin = Math.max(blockHeight - inside, blockHeight / 2)
  if (offsetY <= startMax) return 'start'
  if (offsetY >= endMin) return 'end'
  return 'move'
}
